import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faPen, faCopy, faSave, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import useNotification from '../../hooks/useNotification';
import DataService from '../../services/DataService';
import { QuoteLines } from '../../utils/Database';
import TimeStamp from '../../utils/TimeStamp';
import '../../styles/Modal.css';

const QuoteLineModal = ({ isActive, onClose, quoteId }) => {
    const [opportunity, setOpportunity] = useState(null);
    const [quoteLines, setQuoteLines] = useState([]);
    const [products, setProducts] = useState([]);
    const [editMode, setEditMode] = useState({});
    const [opportunityNameEditMode, setOpportunityNameEditMode] = useState(false);
    const [headlineProduct, setHeadlineProduct] = useState(null);
    const [productTypes, setProductTypes] = useState([]);
    const [lineTypeSelections, setLineTypeSelections] = useState({});
    const [compositeTypeId, setCompositeTypeId] = useState(null);
    const { showNotification } = useNotification();

    const loadQuoteLines = useCallback(async () => {
        try {
            const quoteData = await DataService.getQuoteWithLines(quoteId);
            
            // Charger l'opportunité associée
            if (quoteData.opportunityId) {
                const oppData = await DataService.getById('opportunities', quoteData.opportunityId);
                setOpportunity(oppData);
            }
            
            // Trier les lignes principales et les sous-lignes par position
            const sortedLines = (quoteData.lines || []).sort((a, b) => (a.position || 0) - (b.position || 0));
            sortedLines.forEach(line => {
                if (line.subLines) {
                    line.subLines.sort((a, b) => (a.position || 0) - (b.position || 0));
                }
            });
            setQuoteLines(sortedLines);
        } catch (error) {
            showNotification('Erreur lors du chargement des lignes', 'error');
        }
    }, [quoteId, showNotification]);

    const loadProductTypes = useCallback(async () => {
        try {
            const types = await DataService.getTypes('ProductType');
            setProductTypes(types);
            const compositeType = types.find(type => type.name === 'Composite');
            setCompositeTypeId(compositeType ? compositeType.id : null);
        } catch (error) {
            console.error('Erreur lors du chargement des types de produit', error);
            showNotification('Erreur lors du chargement des types de produit', 'error');
        }
    }, [showNotification]);
    const loadProducts = useCallback(async () => {
        try {
            // Récupérer tous les produits en paginant pour éviter la limite IndexedDB
            const pageSize = 500;
            let page = 0;
            const productList = [];

            // Charger page par page jusqu'à ne plus avoir de résultats
            while (true) {
                const chunk = await DataService.getAll('products', page, pageSize);
                if (!chunk.length) break;
                productList.push(...chunk);
                if (chunk.length < pageSize) break;
                page += 1;
            }
            const currentDate = TimeStamp.fromDate(new Date());

            const toTimeStamp = (value) => {
                if (!value) return null;
                return value instanceof TimeStamp ? value : TimeStamp.fromString(value);
            };

            // Garder les produits actifs même si les dates sont manquantes
            const filteredProducts = productList.filter(product => {
                const validFrom = toTimeStamp(product.validFrom);
                const untilTo = toTimeStamp(product.untilTo);
                const afterStart = !validFrom || TimeStamp.compare(validFrom, currentDate) <= 0;
                const beforeEnd = !untilTo || TimeStamp.compare(currentDate, untilTo) < 0;
                return afterStart && beforeEnd;
            });

            setProducts(filteredProducts);
            const headlineProduct = filteredProducts.find(p => p.name === 'HEADLINE');
            if (!headlineProduct) {
                console.warn('Produit HEADLINE non trouvé');
            }
            setHeadlineProduct(headlineProduct || null);
            setCompositeTypeId(prev => prev ?? (headlineProduct ? headlineProduct.typeId : null));
        } catch (error) {
            console.error('Erreur lors du chargement des produits', error);
            showNotification('Erreur lors du chargement des produits', 'error');
        }
    }, [showNotification]);

    useEffect(() => {
        if (isActive && quoteId) {
            loadProductTypes();
            loadProducts();
            loadQuoteLines();
        }
    }, [isActive, quoteId, loadQuoteLines, loadProducts, loadProductTypes]);

    const compositeTypes = useMemo(() => {
        if (!productTypes.length) return [];
        if (compositeTypeId) {
            return productTypes.filter(type => type.id === compositeTypeId);
        }
        return productTypes.filter(type => type.name === 'Composite');
    }, [productTypes, compositeTypeId]);

    const nonCompositeTypes = useMemo(() => {
        if (!productTypes.length) return [];
        if (compositeTypeId) {
            return productTypes.filter(type => type.id !== compositeTypeId);
        }
        return productTypes.filter(type => type.name !== 'Composite');
    }, [productTypes, compositeTypeId]);

    const availableProductTypeIds = useMemo(() => {
        const ids = new Set();
        products.forEach(product => {
            if (product && product.typeId !== undefined && product.typeId !== null) {
                ids.add(product.typeId);
            }
        });
        return ids;
    }, [products]);

    const getTypeLabel = useCallback((type) => {
        if (!type) return '';
        const typeData = type.data;

        if (typeof typeData === 'string' && typeData.trim().length > 0) {
            return typeData;
        }

        if (typeData && typeof typeData === 'object') {
            if (typeof typeData.label === 'string' && typeData.label.trim().length > 0) {
                return typeData.label;
            }
            if (typeof typeData.name === 'string' && typeData.name.trim().length > 0) {
                return typeData.name;
            }
        }

        return type.name;
    }, []);

    const filterTypesWithProducts = useCallback((types) => {
        if (!types || !types.length) return [];
        return types.filter(type => availableProductTypeIds.has(type.id));
    }, [availableProductTypeIds]);

    const getTypeOptions = useCallback((isSubLine) => {
        if (isSubLine) {
            if (nonCompositeTypes.length) {
                return filterTypesWithProducts(nonCompositeTypes);
            }
            return filterTypesWithProducts(productTypes.filter(type => type.id !== compositeTypeId));
        }
        if (compositeTypes.length) {
            return filterTypesWithProducts(compositeTypes);
        }
        if (compositeTypeId) {
            return filterTypesWithProducts(productTypes.filter(type => type.id === compositeTypeId));
        }
        return filterTypesWithProducts(productTypes);
    }, [compositeTypes, nonCompositeTypes, productTypes, compositeTypeId, filterTypesWithProducts]);

    useEffect(() => {
        if (!quoteLines.length) {
            if (Object.keys(lineTypeSelections).length) {
                setLineTypeSelections({});
            }
            return;
        }

        const newSelections = {};

        const assignSelections = (lines, isSubLine = false) => {
            lines.forEach(line => {
                const allowedTypes = getTypeOptions(isSubLine);
                const allowedIds = allowedTypes.map(type => type.id);
                const product = line.productId ? products.find(p => p.id === line.productId) : null;
                let typeId = null;

                if (product && (allowedIds.length === 0 || allowedIds.includes(product.typeId))) {
                    typeId = product.typeId;
                } else {
                    const previousSelection = lineTypeSelections[line.id];
                    if (previousSelection && (allowedIds.length === 0 || allowedIds.includes(previousSelection))) {
                        typeId = previousSelection;
                    } else if (allowedIds.length > 0) {
                        typeId = allowedIds[0];
                    } else if (product) {
                        typeId = product.typeId;
                    }
                }

                newSelections[line.id] = typeId ?? null;

                if (line.subLines && line.subLines.length > 0) {
                    assignSelections(line.subLines, true);
                }
            });
        };

        assignSelections(quoteLines);

        const currentKeys = Object.keys(lineTypeSelections);
        const newKeys = Object.keys(newSelections);
        const isDifferent =
            currentKeys.length !== newKeys.length ||
            newKeys.some(key => newSelections[key] !== lineTypeSelections[key]);

        if (isDifferent) {
            setLineTypeSelections(newSelections);
        }
    }, [quoteLines, products, lineTypeSelections, getTypeOptions]);

    const handleProductChange = async (lineId, productId) => {
        try {
            if (!productId) {
                await applyAndPersistLineChanges(lineId, { productId: null });
                return;
            }

            const parsedProductId = parseInt(productId, 10);
            const product = products.find(p => p.id === parsedProductId);
            if (!product) return;

            setLineTypeSelections(prev => ({ ...prev, [lineId]: product.typeId }));

            await applyAndPersistLineChanges(lineId, {
                productId: parsedProductId,
                description: product.description,
                unitPrice: product.price,
                quantity: 1
            });
        } catch (error) {
            console.error('Error updating product for line:', error);
            showNotification('Erreur lors de la sélection du produit', 'error');
        }
    };

    const handleTypeChange = async (lineId, typeId) => {
        try {
            const line = findLineById(quoteLines, lineId);
            if (!line) return;

            const parsedTypeId = typeId ? parseInt(typeId, 10) : null;
            const isSubLine = Boolean(line.parentLineId);
            const allowedTypes = getTypeOptions(isSubLine);

            if (parsedTypeId && allowedTypes.length && !allowedTypes.some(type => type.id === parsedTypeId)) {
                showNotification('Type de produit invalide pour cette ligne', 'error');
                return;
            }

            setLineTypeSelections(prev => ({ ...prev, [lineId]: parsedTypeId }));

            if (line.productId) {
                const product = products.find(p => p.id === line.productId);
                if (!product || product.typeId !== parsedTypeId) {
                    await applyAndPersistLineChanges(lineId, { productId: null });
                }
            }
        } catch (error) {
            console.error('Error updating line type:', error);
            showNotification('Erreur lors de la mise à jour du type de produit', 'error');
        }
    };

    const updateLineInHierarchy = (lines, lineId, updates) => {
        return lines.map(line => {
            if (line.id === lineId) {
                return { ...line, ...updates };
            }
            if (line.subLines) {
                return {
                    ...line,
                    subLines: updateLineInHierarchy(line.subLines, lineId, updates)
                };
            }
            return line;
        });
    };

    const calculateTotal = (line) => {
        if (line.subLines && line.subLines.length > 0) {
            return line.subLines.reduce((sum, subLine) => sum + calculateTotal(subLine), 0);
        }
        return line.quantity * line.unitPrice;
    };

    const addMainLine = async () => {
        try {
            if (!headlineProduct) {
                showNotification('Produit HEADLINE manquant : impossible d\'ajouter une ligne principale', 'error');
                return;
            }
            // Calculer la nouvelle position
            const maxPosition = Math.max(...quoteLines.map(line => line.position || 0), -1);
            const newLine = new QuoteLines(
                headlineProduct.description,
                quoteId,
                null,
                headlineProduct.price,
                1,
                headlineProduct.id,
                maxPosition + 1  // Nouvelle position
            );

            const savedLine = await newLine.save();
            setEditMode(prev => ({ ...prev, [savedLine.id]: true }));
            await loadQuoteLines();
        } catch (error) {
            console.error('Error adding main line:', error);
            showNotification('Erreur lors de l\'ajout de la ligne', 'error');
        }
    };

    const addSubLine = async (parentLineId) => {
        try {
            const parentLine = findLineById(quoteLines, parentLineId);
            const maxPosition = Math.max(...(parentLine.subLines || []).map(line => line.position || 0), -1);
            const newLine = new QuoteLines(
                '',
                quoteId,
                parentLineId,
                0,
                1,
                null,
                maxPosition + 1  // Nouvelle position
            );
            const savedLine = await newLine.save();
            setEditMode(prev => ({ ...prev, [savedLine.id]: true }));
            await loadQuoteLines();
        } catch (error) {
            console.error('Error adding sub line:', error);
            showNotification('Erreur lors de l\'ajout de la sous-ligne', 'error');
        }
    };

    const deleteLine = async (lineId) => {
        try {
            const lineToDelete = findLineById(quoteLines, lineId);
            if (!lineToDelete) return;

            // Récupérer toutes les lignes du même niveau
            const siblingLines = lineToDelete.parentLineId 
                ? findLineById(quoteLines, lineToDelete.parentLineId).subLines 
                : quoteLines;

            // Récupérer la position de la ligne à supprimer
            const deletedPosition = lineToDelete.position;

            // Supprimer la ligne
            await DataService.delete('quoteLines', lineId);

            // Mettre à jour les positions des lignes suivantes
            const linesToUpdate = siblingLines.filter(line => 
                line.id !== lineId && line.position > deletedPosition
            );

            // Réduire la position de chaque ligne suivante de 1
            for (const line of linesToUpdate) {
                await DataService.updateQuoteLinePosition(
                    line.id,
                    line.position - 1,
                    quoteId,
                    lineToDelete.parentLineId
                );
            }

            // Recharger les lignes pour mettre à jour l'interface
            await loadQuoteLines();
        } catch (error) {
            console.error('Error deleting line:', error);
            showNotification('Erreur lors de la suppression de la ligne', 'error');
        }
    };

    const copyLine = async (line) => {
        try {
            // Calculer la nouvelle position pour la ligne clonée
            const lines = line.parentLineId 
                ? findLineById(quoteLines, line.parentLineId).subLines 
                : quoteLines;
            const maxPosition = Math.max(...lines.map(l => l.position || 0), -1);

            // Créer la copie avec la nouvelle position
            const copyLine = new QuoteLines(
                line.description,
                line.quoteId,
                line.parentLineId,
                line.unitPrice,
                line.quantity,
                line.productId,
                maxPosition + 1  // Ajouter la nouvelle position
            );
            await copyLine.save();

            // Copier les sous-lignes si elles existent
            if (line.subLines && line.subLines.length > 0) {
                for (const subLine of line.subLines) {
                    const copySubLine = new QuoteLines(
                        subLine.description,
                        subLine.quoteId,
                        copyLine.id,
                        subLine.unitPrice,
                        subLine.quantity,
                        subLine.productId,
                        subLine.position  // Conserver la même position relative pour les sous-lignes
                    );
                    await copySubLine.save();
                }
            }

            // Recharger les lignes pour mettre à jour l'interface
            await loadQuoteLines();
        } catch (error) {
            console.error('Error copying line:', error);
            showNotification('Erreur lors de la copie de la ligne', 'error');
        }
    };

    const moveLine = async (lineId, direction) => {
        try {
            const currentLine = findLineById(quoteLines, lineId);
            if (!currentLine) return;

            const lines = currentLine.parentLineId 
                ? findLineById(quoteLines, currentLine.parentLineId).subLines 
                : quoteLines;

            const currentIndex = lines.findIndex(l => l.id === lineId);
            const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

            if (newIndex < 0 || newIndex >= lines.length) return;

            const adjacentLine = lines[newIndex];

            // Échanger les positions
            const tempPosition = currentLine.position;
            currentLine.position = adjacentLine.position;
            adjacentLine.position = tempPosition;

            // Mettre à jour dans la base de données
            await DataService.updateQuoteLinePosition(lineId, currentLine.position, quoteId, currentLine.parentLineId);
            await DataService.updateQuoteLinePosition(adjacentLine.id, adjacentLine.position, quoteId, adjacentLine.parentLineId);

            // Recharger les lignes pour refléter le nouvel ordre
            await loadQuoteLines();
        } catch (error) {
            console.error('Error moving line:', error);
            showNotification('Erreur lors du déplacement de la ligne', 'error');
        }
    };

    const renderLineRow = (line, isSubLine = false, lines = []) => {
        const selectedTypeId = lineTypeSelections[line.id] ?? '';
        const typeOptions = getTypeOptions(isSubLine);
        const filteredProducts = selectedTypeId
            ? products.filter(product => product.typeId === selectedTypeId)
            : [];
        const fallbackProducts = products.filter(product => {
            if (isSubLine) {
                return compositeTypeId ? product.typeId !== compositeTypeId : true;
            }
            return compositeTypeId ? product.typeId === compositeTypeId : true;
        });
        const productOptions = filteredProducts.length ? filteredProducts : fallbackProducts;

        return (
            <tr key={line.id}>
                <td>
                    <div className="select is-fullwidth">
                        <select
                            value={selectedTypeId || ''}
                            onChange={(e) => handleTypeChange(line.id, e.target.value)}
                            disabled={!editMode[line.id]}
                        >
                            <option value="">Sélectionner un type</option>
                            {typeOptions.map(type => (
                                <option key={type.id} value={type.id}>
                                    {getTypeLabel(type)}
                                </option>
                            ))}
                        </select>
                    </div>
                </td>
                <td>
                    <div className="select is-fullwidth">
                        <select
                            value={line.productId || ''}
                            onChange={(e) => handleProductChange(line.id, e.target.value)}
                            disabled={!editMode[line.id]}
                        >
                            <option value="">Sélectionner un produit</option>
                            {productOptions.map(product => (
                                <option key={product.id} value={product.id}>
                                    {product.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </td>
                <td>
                    <input style={{ fontWeight: isSubLine ? 'normal' : 'bold', fontSize: isSubLine ? '0.90rem' : '1.10rem', paddingLeft: isSubLine ? '20px' : '0' }}
                        className="input"
                        type="text"
                        value={line.description || ''}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        disabled={!editMode[line.id]}
                    />
                </td>
                <td>
                    {isSubLine ? (
                        <input style={{ textAlign: 'right' }}
                            className="input"
                            type="number"
                            value={line.unitPrice || 0}
                            onChange={(e) => updateLine(line.id, 'unitPrice', parseFloat(e.target.value))}
                            disabled={!editMode[line.id] && !isSubLine}
                        />
                    ) : (
                        <span style={{ fontWeight : 'bold', textAlign: 'center' }}>-</span>
                    )}
                </td>
                <td>
                    {isSubLine ? (
                        <input style={{ textAlign: 'right' }}
                            className="input"
                            type="number"
                            value={line.quantity || 1}
                            onChange={(e) => updateLine(line.id, 'quantity', parseInt(e.target.value))}
                            disabled={!editMode[line.id] && !isSubLine}
                        />
                    ) : (
                        <span style={{ fontWeight : 'bold', textAlign: 'center' }}>-</span>
                    )}
                </td>
                <td style={{ textAlign: 'right', fontWeight: isSubLine ? 'normal' : 'bold' }}>{isSubLine ? `${calculateTotal(line).toFixed(2)} €` : `${calculateTotal(line).toFixed(2)} €`}</td>
                <td>
                    <div className="table-actions-container">
                        <button
                            className="button is-small table-action-button"
                            onClick={() => setEditMode({ ...editMode, [line.id]: !editMode[line.id] })}
                        >
                            <span className="icon is-small icon-primary">
                            <FontAwesomeIcon icon={editMode[line.id] ? faSave : faPen} />
                            </span>
                        </button>
                        {!isSubLine && (
                            <button
                                className="button is-small table-action-button"
                                onClick={() => addSubLine(line.id)}
                            >
                                <span className="icon is-small icon-primary">
                                <FontAwesomeIcon icon={faPlus} />
                                </span>
                            </button>
                        )}
                        <button
                            className="button is-small table-action-button"
                            onClick={() => copyLine(line)}
                        >
                            <span className="icon is-small icon-primary">
                            <FontAwesomeIcon icon={faCopy} />
                            </span>
                        </button>
                        {/* Boutons de déplacement */}
                        <button
                            className="button is-small table-action-arrow"
                            onClick={() => moveLine(line.id, 'up')}
                            disabled={line.position === 0}
                        >
                            <span className="icon is-small">
                                <FontAwesomeIcon icon={faArrowUp} />
                            </span>
                        </button>
                        <button
                            className="button is-small table-action-arrow"
                            onClick={() => moveLine(line.id, 'down')}
                            disabled={line.position === lines.length - 1}
                        >
                            <span className="icon is-small">
                                <FontAwesomeIcon icon={faArrowDown} />
                            </span>
                        </button>
                        <button
                            className="button is-small table-action-button"
                            onClick={() => deleteLine(line.id)}
                        >
                            <span className="icon is-small icon-danger">
                            <FontAwesomeIcon icon={faTrashAlt} />
                            </span>
                        </button>
                    </div>
                </td>
            </tr>
        );
    };

    const applyAndPersistLineChanges = async (lineId, updates) => {
        let updatedLine = null;

        setQuoteLines(prevLines => {
            const newLines = updateLineInHierarchy(prevLines, lineId, updates);
            updatedLine = findLineById(newLines, lineId);
            return newLines;
        });

        if (!updatedLine) {
            const existingLine = await DataService.getById('quoteLines', lineId);
            if (existingLine) {
                updatedLine = { ...existingLine, ...updates };
            }
        }

        if (updatedLine) {
            await DataService.update('quoteLines', lineId, updatedLine);
        }

        return updatedLine;
    };

    const updateLine = async (lineId, field, value) => {
        try {
            // Forcer la valeur 0 pour unitPrice si vide ou null
            if (field === 'unitPrice' && (value === null || value === '' || isNaN(value))) {
                value = 0;
            }

            await applyAndPersistLineChanges(lineId, { [field]: value });
        } catch (error) {
            console.error('Error updating line:', error);
            showNotification('Erreur lors de la mise à jour de la ligne', 'error');
        }
    };

    // Nouvelle fonction utilitaire pour trouver une ligne par ID
    const findLineById = (lines, lineId) => {
        for (const line of lines) {
            if (line.id === lineId) return line;
            if (line.subLines) {
                const found = findLineById(line.subLines, lineId);
                if (found) return found;
            }
        }
        return null;
    };

        // Ajouter cette fonction pour calculer le total général
        const calculateGrandTotal = () => {
            let total = 0;
            quoteLines.forEach(line => {
                // Ajouter le total des sous-lignes
                if (line.subLines && line.subLines.length > 0) {
                    line.subLines.forEach(subLine => {
                        total += calculateTotal(subLine);
                    });
                }
            });
            return total;
        };

        const updateOpportunityName = async (newName) => {
            try {
                if (!opportunity) return;
                
                // Mettre à jour l'opportunité dans la base de données
                const updatedOpportunity = { ...opportunity, name: newName };
                await DataService.update('opportunities', opportunity.id, { name: newName });
                
                // Mettre à jour l'état local
                setOpportunity(updatedOpportunity);
                showNotification('Nom du projet mis à jour avec succès', 'success');
            } catch (error) {
                console.error('Error updating opportunity name:', error);
                showNotification('Erreur lors de la mise à jour du nom du projet', 'error');
            }
        };

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose}></div>
            <div className="modal-card modal-card-wide">
                <header className="modal-card-head">
                    <p className="modal-card-title">Lignes de devis</p>
                    <button className="delete" aria-label="close" onClick={onClose}></button>
                </header>
                <section className="modal-card-body">
                    <div className="modal-subpanel">
                        <div className="modal-subpanel-header">
                            <div className="quote-name-total-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <span className="opportunity-name-label">Nom du projet: </span>
                                    {opportunityNameEditMode ? (
                                        <input
                                            className="input opportunity-name-input"
                                            type="text"
                                            value={opportunity?.name || ''}
                                            onChange={(e) => setOpportunity(prev => ({ ...prev, name: e.target.value }))}
                                            onBlur={() => {
                                                if (opportunity) {
                                                    updateOpportunityName(opportunity.name);
                                                }
                                                setOpportunityNameEditMode(false);
                                            }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    if (opportunity) {
                                                        updateOpportunityName(opportunity.name);
                                                    }
                                                    setOpportunityNameEditMode(false);
                                                }
                                            }}
                                            autoFocus
                                            style={{ minWidth: '200px', marginBottom: '0', marginRight: '5px' }}
                                        />
                                    ) : (
                                        <span
                                            className="opportunity-name-display"
                                            onClick={() => setOpportunityNameEditMode(true)}
                                            style={{
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                fontWeight: 'bold',
                                                maxWidth: '300px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={opportunity?.name || 'Nom du projet'}
                                        >
                                            {opportunity?.name || 'Nom du projet'}
                                        </span>
                                    )}
                                    {opportunityNameEditMode && (
                                        <button
                                            className="button is-small"
                                            onClick={() => {
                                                if (opportunity) {
                                                    updateOpportunityName(opportunity.name);
                                                }
                                                setOpportunityNameEditMode(false);
                                            }}
                                            style={{ marginBottom: '0' }}
                                        >
                                            <span className="icon is-small icon-primary">
                                                <FontAwesomeIcon icon={faSave} />
                                            </span>
                                        </button>
                                    )}
                                </div>
                                <span className="total-label">Total du devis : {calculateGrandTotal().toFixed(2)} €</span>
                            </div>
                            <button className="button is-small" onClick={addMainLine}>
                                <span className="icon is-small icon-primary">
                                    <FontAwesomeIcon icon={faPlus} />
                                </span>
                            </button>
                        </div>
                        <div className="table-container">
                            <table className="table is-fullwidth is-striped is-hoverable mb-0">
                                <thead className="modal-table-header">
                                    <tr>
                                        <th style={{ width: '140px' }}>Type</th>
                                        <th style={{ width: '150px' }}>Produit</th>
                                        <th>Description</th>
                                        <th style={{ width: '50px' }}>P.U.</th>
                                        <th style={{ width: '50px' }}>Q.</th>
                                        <th style={{ width: '120px' }}>Total</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quoteLines.map((line, index) => (
                                        <React.Fragment key={line.id}>
                                            {renderLineRow(line, false, quoteLines)}
                                            {line.subLines?.map((subLine, subIndex) => (
                                                renderLineRow(subLine, true, line.subLines)
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
                <footer className="modal-card-foot">
                    <button className="button" onClick={onClose}>Fermer</button>
                </footer>
            </div>
        </div>
    );
};

export default QuoteLineModal;
